/**
 * The guide stepper (#436): a guide opens in a modal, one screen at a time —
 * the intro (when the guide has one), each step, then a done screen. Pure
 * functions; `components/guides/guide-modal.tsx` renders them.
 *
 * Ticking rules (owner decision):
 * - Next on an `info` step ticks it;
 * - `action` and `auto` steps tick themselves when their block completes
 *   (`blockCompletion`) — Next never ticks them, and is always allowed;
 * - a company step (`audience: admins`) is ticked through the company
 *   endpoint. Members never see one (the API leaves them out; so does
 *   `stepperSteps`, as defence in depth).
 */
import type { GuideStatus } from "@nodus/ui";
import type { GuideStep } from "@/lib/api/guides";
import { pickStore, readKey, writeKey, type KeyValueStore } from "@/lib/login-session";
import { blockCompletion, memberSteps } from "@/lib/guides";

export type StepperScreen =
  | { kind: "intro"; id: "intro" }
  | { kind: "step"; id: string; step: GuideStep; number: number; total: number }
  | { kind: "done"; id: "done" };

const stepId = (step: Pick<GuideStep, "key">) => `step:${step.key}`;

/** The steps the stepper shows this viewer. */
export function stepperSteps(steps: GuideStep[], hasBlock: (key: string) => boolean, isCompanyAdmin: boolean): GuideStep[] {
  return memberSteps(steps, hasBlock).filter((s) => s.audience !== "admins" || isCompanyAdmin);
}

export function stepperScreens(intro: string, steps: GuideStep[]): StepperScreen[] {
  const screens: StepperScreen[] = [];
  if (intro.trim()) screens.push({ kind: "intro", id: "intro" });
  steps.forEach((step, i) => screens.push({ kind: "step", id: stepId(step), step, number: i + 1, total: steps.length }));
  screens.push({ kind: "done", id: "done" });
  return screens;
}

/**
 * A guide the member has finished: it opens on the completion screen. A guide
 * that an important new step reopened (`reopened`) is not finished.
 */
export function isFinished(status: GuideStatus): boolean {
  return status === "done";
}

/**
 * Where the stepper opens: a finished guide on its completion screen; else
 * where the member was earlier in this session; else the first unfinished
 * step of a half-done guide; else the start (a new guide).
 */
export function initialScreenIndex(screens: StepperScreen[], remembered: string | null, finished = false): number {
  if (finished) {
    const end = screens.findIndex((s) => s.kind === "done");
    if (end >= 0) return end;
  }
  if (remembered && remembered !== "done") {
    const at = screens.findIndex((s) => s.id === remembered);
    if (at >= 0) return at;
  }
  const steps = screens.filter((s): s is Extract<StepperScreen, { kind: "step" }> => s.kind === "step");
  const started = steps.some((s) => s.step.done);
  const open = screens.findIndex((s) => s.kind === "step" && !s.step.done);
  if (started && open >= 0) return open;
  return 0;
}

export interface StepTick {
  stepKey: string;
  company: boolean;
}

/** What Next (or Done, on the last step) does from screen `index`. */
export function nextMove(screens: StepperScreen[], index: number): { to: number; tick: StepTick | null } {
  const screen = screens[index];
  const to = Math.min(index + 1, screens.length - 1);
  if (screen?.kind !== "step") return { to, tick: null };
  const { step } = screen;
  const tick = step.behaviour === "info" && !step.done ? { stepKey: step.key, company: step.audience === "admins" } : null;
  return { to, tick };
}

export function backIndex(index: number): number {
  return Math.max(0, index - 1);
}

/**
 * "Go through it again" on the completion screen: back to step 1, for review
 * only — every tick stays (owner ruling), nothing is saved.
 */
export function reviewStartIndex(screens: StepperScreen[]): number {
  const first = screens.findIndex((s) => s.kind === "step");
  return first >= 0 ? first : 0;
}

// --- done steps: what shows, for this visit (#436 done states) -----------------------

/**
 * What the member opened by hand, and the step they arrived on while it was
 * still open. Both last until they leave the step (`arriveAt` starts over), so
 * the rule is one rule: a done step's form stays open while you are on it, if
 * it was open when you came or you pressed Change this. Nothing is saved.
 */
export interface StepVisit {
  /** The step on screen, if it was not done when the member arrived. */
  arrivedOpen: string | null;
  /** The step the member pressed "Change this" on. */
  revealed: string | null;
}

export const NO_VISIT: StepVisit = { arrivedOpen: null, revealed: null };

/** Arriving on a screen: a step that is not done yet stays open while the
 *  member is on it (finishing it there keeps its result on screen). */
export function arriveAt(screen: StepperScreen | undefined): StepVisit {
  return { arrivedOpen: screen?.kind === "step" && !screen.step.done ? screen.step.key : null, revealed: null };
}

export function revealStep(visit: StepVisit, stepKey: string): StepVisit {
  return { ...visit, revealed: stepKey };
}

/** A block finished on screen (a new Wi-Fi login or password may be showing,
 *  once): keep it open, with no Hide, until the member leaves the step. */
export function keepOpen(visit: StepVisit, stepKey: string): StepVisit {
  return { arrivedOpen: stepKey, revealed: visit.revealed === stepKey ? null : visit.revealed };
}

/**
 * What a block's `onCompleted` does: always keep the step open for this visit
 * (`keepOpen`), then tick it or recheck it as `blockCompletion` says.
 *
 * A practice step (`step.practice`, see `blocks/practice.ts`) made nothing
 * real, so there is nothing to recheck: tick it (unless it is done already)
 * and hand over to `practiced`, which moves the guide on and says it was
 * practice. The backend never serves a practice step as `auto`.
 */
export function blockCompletedHandler(
  step: Pick<GuideStep, "key" | "behaviour" | "done" | "audience" | "practice"> & { block_key?: string },
  handlers: {
    keepOpen: (stepKey: string) => void;
    tick: (tick: StepTick) => void;
    refetch: () => void;
    practiced: (stepKey: string) => void;
  },
): () => void {
  const kind = blockCompletion(step);
  return () => {
    if (step.practice) {
      if (!step.done) handlers.tick({ stepKey: step.key, company: step.audience === "admins" });
      handlers.practiced(step.key);
      return;
    }
    handlers.keepOpen(step.key);
    if (kind === "tick") handlers.tick({ stepKey: step.key, company: step.audience === "admins" });
    else if (kind === "refetch") handlers.refetch();
  };
}

/** A practice notice, and the screen it shows on (see `practiceTarget`). */
export interface PracticeNotice {
  at: string;
  text: string;
}

/**
 * Where the guide goes after a practice run on `stepKey`: the next screen —
 * the end screen ("Well done") when it was the last step. -1 when the step
 * isn't on screen any more.
 */
export function practiceTarget(screens: StepperScreen[], stepKey: string): number {
  const at = screens.findIndex((s) => s.id === stepId({ key: stepKey }));
  return at < 0 ? -1 : Math.min(at + 1, screens.length - 1);
}

export function hideStep(visit: StepVisit, stepKey: string): StepVisit {
  return visit.revealed === stepKey ? { ...visit, revealed: null } : visit;
}

/**
 * The done toggle under a step: "change" (collapsed: a ✓ summary with Change
 * this), "hide" (opened with Change this: the form, with Hide), or null (the
 * step shows as normal). Only a done step whose block is a form or an action
 * (`doneMode` "hide") collapses; reading material — Markdown, an FAQ article,
 * an informational block — stays readable, and so does the step the member
 * arrived on while it was open.
 */
export function stepDoneToggle(
  step: Pick<GuideStep, "key" | "done" | "content_kind" | "block_key">,
  doneMode: (blockKey: string) => "hide" | "readonly",
  visit: StepVisit,
): "change" | "hide" | null {
  if (!step.done || step.content_kind !== "block" || doneMode(step.block_key) !== "hide") return null;
  if (visit.arrivedOpen === step.key) return null;
  return visit.revealed === step.key ? "hide" : "change";
}

/** Opens on the completion screen: finished, and every step this viewer sees
 *  agrees. The backend keeps a guide `done` after an auto check turns false
 *  or a company step is unticked; that guide opens on the step that needs them. */
export function opensFinished(status: GuideStatus, steps: GuideStep[]): boolean {
  return isFinished(status) && completion(steps).complete;
}

/** The status the modal header shows: a `done` guide with a step that is no
 *  longer done says "in progress", never FINISHED next to "1 step is not done". */
export function displayStatus(status: GuideStatus, steps: GuideStep[]): GuideStatus {
  return status === "done" && !completion(steps).complete ? "in_progress" : status;
}

/** What the stepper's buttons do. `mark` saves a tick — the only write. */
export function stepperActions(
  screens: StepperScreen[],
  index: number,
  handlers: { mark: (tick: StepTick) => void; go: (index: number) => void; close: () => void },
) {
  return {
    next() {
      if (screens[index]?.kind === "done") return handlers.close();
      const move = nextMove(screens, index);
      if (move.tick) handlers.mark(move.tick);
      handlers.go(move.to);
    },
    back() {
      handlers.go(backIndex(index));
    },
    /** Review only: the ticks stay, nothing is saved (owner ruling). */
    goThroughAgain() {
      handlers.go(reviewStartIndex(screens));
    },
  };
}

/** The done screen: finished, or how many steps are still open. */
export function completion(steps: GuideStep[]): { complete: boolean; remaining: number; firstOpen: string | null } {
  const open = steps.filter((s) => !s.done);
  return {
    complete: steps.length > 0 && open.length === 0,
    remaining: open.length,
    firstOpen: open.length ? stepId(open[0]) : null,
  };
}

/** The label of the main footer button on a screen. */
export function nextLabel(screens: StepperScreen[], index: number): "Next" | "Done" | "Close" {
  const screen = screens[index];
  if (screen?.kind === "done") return "Close";
  return screens[index + 1]?.kind === "done" ? "Done" : "Next";
}

/** "Step 2 of 5", or what the intro and the end screen are. */
export function positionLabel(screen: StepperScreen | undefined): string {
  if (!screen) return "";
  if (screen.kind === "intro") return "Before you start";
  if (screen.kind === "done") return "The end";
  return `Step ${screen.number} of ${screen.total}`;
}

// --- position within the session ---------------------------------------------------

const POSITION_PREFIX = "guide_stepper_at:";

/** Per tab: sessionStorage, else memory. */
function sessionStore(): KeyValueStore {
  return pickStore([() => globalThis.sessionStorage]);
}

export function rememberScreen(guideKey: string, screenId: string, store: KeyValueStore = sessionStore()): void {
  writeKey(store, POSITION_PREFIX + guideKey, screenId);
}

export function rememberedScreen(guideKey: string, store: KeyValueStore = sessionStore()): string | null {
  return readKey(store, POSITION_PREFIX + guideKey);
}

// --- ?guide= : a guide is always a popup over the current page -----------------------

/** The query parameter that opens a guide over any portal page (`?guide=g%3A12`). */
export const GUIDE_PARAM = "guide";

/** An operator (`g:`) or built-in (`b:`) guide key — the backend checks the
 *  same format (`crm.services.announcements._GUIDE_KEY_RE`). */
const GUIDE_KEY_RE = /^[bg]:[A-Za-z0-9_-][A-Za-z0-9._-]{0,63}$/;

export function isGuideKey(key: string): boolean {
  return GUIDE_KEY_RE.test(key);
}

/** The guide key in a location's query, decoded, else null — also for a
 *  malformed key, which is never fetched. */
export function guideKeyFromSearch(search: string): string | null {
  const key = new URLSearchParams(search).get(GUIDE_PARAM);
  return key && isGuideKey(key) ? key : null;
}

/** History state on the entry that opening a guide pushes: closing goes back
 *  to the page's own entry instead of leaving a duplicate behind. */
export const GUIDE_OPENED_STATE = { guideOpened: true } as const;

export function openedByUs(state: unknown): boolean {
  return !!state && typeof state === "object" && (state as { guideOpened?: unknown }).guideOpened === true;
}

/** `search` with the guide set (or removed, with `null`); other params are kept. */
export function withGuide(search: string, key: string | null): string {
  const params = new URLSearchParams(search);
  if (key) params.set(GUIDE_PARAM, key);
  else params.delete(GUIDE_PARAM);
  const out = params.toString();
  return out ? `?${out}` : "";
}

/** The guide an in-app link (an announcement's `portal_path`) opens, else null. */
export function guideKeyFromLink(path: string): string | null {
  const at = path.indexOf("?");
  return at >= 0 ? guideKeyFromSearch(path.slice(at)) : null;
}

/**
 * Whether Esc closes the guide: only when the guide is the topmost dialog.
 * Focus inside the guide → yes; focus in something a block or the header menu
 * opened on top (the room finder, a dropdown) → no, that one closes instead;
 * focus nowhere → only if no other dialog is open.
 */
export function escapeClosesGuide(
  guide: Pick<Node, "contains"> | null,
  active: unknown,
  body: unknown,
  openDialogs: number,
): boolean {
  if (!guide) return false;
  if (active && active !== body) return guide.contains(active as Node);
  return openDialogs <= 1;
}
